import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarClock, ListTodo, ChevronRight, Rocket, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";

const Box = ({ title, icon: Icon, children, onAll, testid }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm min-w-0" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Icon className="w-4 h-4 text-emerald-600" />{title}</div>
      {onAll && <button onClick={onAll} className="text-xs text-emerald-700 inline-flex items-center">Tout voir <ChevronRight className="w-3.5 h-3.5" /></button>}
    </div>
    {children}
  </div>
);

export function TodayWidget() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const s = new Date(); s.setHours(0, 0, 0, 0); const e = new Date(s); e.setDate(e.getDate() + 1);
    api.get("/appointments", { params: { start: s.toISOString(), end: e.toISOString() } }).then((r) => setRows((r.data || []).filter((a) => !(a.is_block && !a.client_id)).sort((a, b) => a.start.localeCompare(b.start)))).catch(() => setRows([]));
  }, []);
  const inProgress = (rows || []).filter((a) => a.status === "en_cours");
  return (
    <Box title={`Rendez-vous du jour${rows ? ` (${rows.length})` : ""}`} icon={CalendarClock} onAll={() => navigate("/today")} testid="widget-today">
      {inProgress.length > 0 && <div className="mb-2 text-xs font-semibold text-blue-700" data-testid="widget-in-progress">{inProgress.length} intervention(s) en cours</div>}
      <div className="space-y-1.5">
        {(rows || []).slice(0, 6).map((a) => (
          <button key={a.id} onClick={() => navigate(`/calendar?appt=${a.id}`)} className="w-full flex items-center gap-2 text-left rounded-lg px-2 py-1.5 hover:bg-slate-50">
            <span className="text-xs font-mono text-slate-500 w-11 shrink-0">{a.all_day ? "Jour" : new Date(a.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
            <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">{a.client_name || a.title}</span>
            <StatusBadge status={a.status} />
          </button>
        ))}
        {rows && !rows.length && <p className="text-sm text-slate-400">Aucun rendez-vous aujourd'hui.</p>}
      </div>
    </Box>
  );
}

export function StartWidget() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/onboarding/checklist").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d || d.done === d.total) return null;
  const starter = async () => {
    setBusy(true);
    try { const { data } = await api.post("/onboarding/starter-services"); toast.success(data.created.length ? `${data.created.length} prestation(s) ajoutée(s) — complétez leurs tarifs` : "Ces prestations existent déjà"); load(); }
    catch { toast.error("Action réservée à l'administrateur"); } finally { setBusy(false); }
  };
  const servicesDone = d.items.find((i) => i.key === "services")?.done;
  return (
    <Box title={`Bien démarrer${d.profession_label ? ` — ${d.profession_label}` : ""} (${d.done}/${d.total})`} icon={Rocket} testid="widget-start">
      <div className="h-1.5 rounded-full bg-slate-100 mb-3"><div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${(d.done / d.total) * 100}%` }} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {d.items.map((i) => (
          <button key={i.key} data-testid={`start-step-${i.key}`} onClick={() => navigate(i.link)} className="flex items-center gap-2 text-left text-sm rounded-lg px-2 py-1.5 hover:bg-slate-50">
            {i.done ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
            <span className={i.done ? "text-slate-400 line-through" : "text-slate-700"}>{i.label}</span>
          </button>
        ))}
      </div>
      {!servicesDone && <button data-testid="start-starter-services" disabled={busy} onClick={starter} className="mt-3 text-xs font-semibold rounded-lg bg-emerald-500 text-black px-3 py-2">Ajouter les prestations de départ de mon métier ({d.starter_services.join(", ")})</button>}
      {!!d.tips?.length && <ul className="mt-3 text-[11px] text-slate-500 list-disc pl-4 space-y-0.5">{d.tips.map((t) => <li key={t}>{t}</li>)}</ul>}
    </Box>
  );
}

export function TasksWidget() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/tasks").then((r) => setRows((r.data || []).filter((t) => t.status !== "termine"))).catch(() => setRows([])); }, []);
  const PR = { haute: "bg-red-100 text-red-700", normale: "bg-slate-100 text-slate-600", basse: "bg-slate-50 text-slate-400" };
  return (
    <Box title={`Tâches à faire${rows ? ` (${rows.length})` : ""}`} icon={ListTodo} onAll={() => navigate("/taches")} testid="widget-tasks">
      <div className="space-y-1.5">
        {(rows || []).slice(0, 6).map((t) => (
          <div key={t.id} className="flex items-center gap-2 text-sm px-2 py-1">
            <span className="flex-1 min-w-0 truncate text-slate-800">{t.title}</span>
            {t.due_date && <span className="text-[11px] text-slate-500 shrink-0">{new Date(t.due_date).toLocaleDateString("fr-FR")}</span>}
            <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${PR[t.priority] || PR.normale}`}>{t.priority}</span>
          </div>
        ))}
        {rows && !rows.length && <p className="text-sm text-slate-400">Aucune tâche en attente.</p>}
      </div>
    </Box>
  );
}
